import { SelectWorkflowComponent } from './common/selectcomponent';
import { dedent } from 'ts-dedent';
import { ColumnDataType, Provider, TokenAllowedApis, WorkflowComponentParamType, WorkflowParameterValueErrorType } from '../types';
import { containsExpressionValue, isExpressionValue } from '../utils';
import { addColumn } from '../utils/schemas';
const placeholdersRegex = /{{(\b\w+\b)}}/g;
class HTTPRequest extends SelectWorkflowComponent {
    constructor() {
        super({
            name: 'native.httprequest',
            group: 'Input/Output',
            title: 'HTTP Request',
            description: `
      This component perform requests to external endpoints.
      `,
            starred: true,
            warningTitle: `API Key security warning`,
            warningDescription: `Including an API key or an authorization header in the URL or options object may expose them in data warehouse logs. Handle keys responsibly to prevent unauthorized access.`
        }, [
            {
                name: 'source',
                type: WorkflowComponentParamType.InputTable,
                title: 'Source table',
                optional: true
            },
            {
                name: 'url',
                type: WorkflowComponentParamType.String,
                title: 'URL',
                allowExpressions: true
            },
            {
                name: 'options',
                type: WorkflowComponentParamType.Json,
                title: 'Options',
                placeholder: dedent `E.g.:
                                  {
                                    "method":"GET",
                                    "headers":{
                                      "Content-Type":"text/plain"
                                    },
                                    "mode":"cors",
                                    "cache":"default"
                                  }`,
                allowExpressions: true,
                optional: true
            },
            {
                name: 'allowedhosts',
                type: WorkflowComponentParamType.String,
                title: 'Allowed hosts',
                placeholder: 'example.com,example2.com',
                optional: true,
                advanced: true
            }
        ]);
        this.supportedProviders = [
            Provider.BigQuery,
            Provider.Snowflake,
            Provider.Redshift
        ];
    }
    convertToBigQueryFormat(input) {
        // Replace placeholders in input string
        const formattedInput = input.replace(placeholdersRegex, '%s');
        // Extract placeholders from input string
        const placeholders = [];
        let match;
        while ((match = placeholdersRegex.exec(input)) !== null) {
            placeholders.push(match[1]);
        }
        // Create the BigQuery format string
        const placeholdersString = placeholders
            .map((key) => `COALESCE(${key}, '""')`)
            .join(',\n');
        return `FORMAT('''${formattedInput}''',\n${placeholdersString}\n)`;
    }
    convertToSnowflakeFormat(input) {
        // Replace placeholders in input string
        const formattedInput = input.replace(placeholdersRegex, (match, placeholder) => {
            return `', COALESCE(${placeholder}, '""'), '`;
        });
        // Create the Snowflake format string
        const snowflakeFormat = `CONCAT('${formattedInput}')`;
        return snowflakeFormat;
    }
    convertToRedshiftFormat(input) {
        // Replace placeholders in input string
        const formattedInput = input.replace(placeholdersRegex, (match, placeholder) => {
            return `' || COALESCE(${placeholder}, '""') || '`;
        });
        // Create the Redshift format string
        const redshiftFormat = `'${formattedInput}'`;
        return redshiftFormat;
    }
    convertToProviderFormat(input, provider) {
        switch (provider) {
            case Provider.BigQuery:
                return this.convertToBigQueryFormat(input);
            case Provider.Snowflake:
                return this.convertToSnowflakeFormat(input);
            case Provider.Redshift:
                return this.convertToRedshiftFormat(input);
            default:
                return input;
        }
    }
    getInnerSql({ workflow, componentName, params, connection, envVars, codeType }) {
        if (connection.type !== Provider.BigQuery &&
            connection.type !== Provider.Snowflake &&
            connection.type !== Provider.Redshift) {
            return null;
        }
        const preparedParams = this._prepareParameters(params);
        const includeSourceColumns = preparedParams.source ? '*, ' : '';
        let query = '';
        let url = 'NULL';
        if (preparedParams.url) {
            if (isExpressionValue(workflow.componentValues[componentName].url)) {
                url = preparedParams.url;
            }
            else {
                url = `'${preparedParams.url}'`;
            }
        }
        let options = `'{"headers":{"Content-Type":"text/plain"}}'`;
        if (preparedParams.options) {
            const originalOptions = workflow.componentValues[componentName]
                .options.value;
            if (containsExpressionValue(originalOptions)) {
                options = this.convertToProviderFormat(originalOptions, connection.type).replace(/\\"/g, '\\\\"');
            }
            else {
                options = `'${JSON.stringify(JSON.parse(preparedParams.options))}'`.replace(/\\"/g, '\\\\"');
            }
        }
        switch (connection.type) {
            case Provider.BigQuery:
                query = dedent `
          SELECT ${includeSourceColumns}${connection.quoteName(envVars.analyticsToolboxDataset)}.HTTP_REQUEST(
            '${envVars.apiBaseUrl}',
            '${envVars.accessToken}',
            ${url},
            ${options}) as response_data
            ${preparedParams.source
                    ? `FROM ${connection.quoteName(preparedParams.source)}`
                    : ''}
          `;
                break;
            case Provider.Redshift:
            case Provider.Snowflake:
                query = dedent `
          SELECT ${includeSourceColumns}${connection.quoteName(envVars.analyticsToolboxDataset)}.HTTP_REQUEST(
            ${url},
            ${options},
            '${envVars.apiBaseUrl}',
            '${envVars.accessToken}') as response_data
            ${preparedParams.source
                    ? `FROM ${connection.quoteName(preparedParams.source)}`
                    : ''}
          `;
                break;
        }
        return query;
    }
    getRequiredGrants({ params }) {
        const regex = /^\(.*\)$/;
        const url = params.url;
        if (regex.test(url)) {
            const allowedHosts = params.allowedhosts;
            const hosts = allowedHosts.split(',');
            const grants = hosts.map((host) => {
                return { host };
            });
            return {
                grants: grants,
                apis: [TokenAllowedApis.httpRequest]
            };
        }
        else {
            return {
                grants: [{ host: new URL(params.url).host }],
                apis: [TokenAllowedApis.httpRequest]
            };
        }
    }
    checkInputValues({ params, resolvedParamValues, connection, dryRun, schemas, variables }) {
        const problems = super.checkInputValues({
            params,
            resolvedParamValues,
            connection,
            dryRun,
            schemas,
            variables
        });
        if (resolvedParamValues.url) {
            if (isExpressionValue(params.url)) {
                if (!resolvedParamValues.allowedhosts) {
                    problems.push({
                        parameter: 'url',
                        type: WorkflowParameterValueErrorType.MissingValue,
                        message: 'Allowed hosts must be introduced when using expressions in the URL'
                    });
                }
            }
            else {
                let validUrl = true;
                try {
                    if (!new URL(resolvedParamValues.url).host) {
                        validUrl = false;
                    }
                }
                catch (e) {
                    validUrl = false;
                }
                if (!validUrl) {
                    problems.push({
                        parameter: 'url',
                        type: WorkflowParameterValueErrorType.WrongValue,
                        message: 'Invalid URL'
                    });
                }
            }
        }
        if (resolvedParamValues.options &&
            !containsExpressionValue(params.options)) {
            try {
                JSON.parse(resolvedParamValues.options);
            }
            catch (e) {
                problems.push({
                    parameter: 'options',
                    type: WorkflowParameterValueErrorType.WrongValue,
                    message: 'Invalid JSON string'
                });
            }
        }
        return problems;
    }
    requiredAnalyticsToolboxVersion(provider) {
        switch (provider) {
            case Provider.BigQuery:
                return '1.14.0';
            case Provider.Snowflake:
                return '1.12.0';
            case Provider.Redshift:
                return '1.7.0';
        }
        return '1.0.0';
    }
    getVersion(provider) {
        return '1';
    }
    canProvideOutputSchemas() {
        return true;
    }
    getOutputSchema({ columns }) {
        return addColumn(columns.source, {
            name: 'response_data',
            type: ColumnDataType.String
        });
    }
}
export { HTTPRequest };
